<template>
  <div class="forgot-password-form">
    <!-- Error Alert -->
    <div v-if="hasError" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle"></i>
      {{ errorMessage }}
    </div>

    <!-- Success Alert -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      <i class="fas fa-check-circle"></i>
      {{ successMessage }}
    </div>

    <form @submit.prevent="handleSubmit" class="auth-form">
      <div class="form-group">
        <input
          id="email"
          v-model="email"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': errors.email }"
          placeholder="Enter your email"
          required
          :disabled="isLoading"
        />
        <div v-if="errors.email" class="invalid-feedback">
          {{ errors.email[0] }}
        </div>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="loading-spinner"></span>
        {{ isLoading ? "Sending Reset Link..." : "Send Reset Link" }}
      </button>
    </form>

    <div class="form-footer">
      <p class="signin-link">
        Remember your password?
        <router-link :to="`/${currentLocale}/auth/login`" class="link-primary">
          Sign in here
        </router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AuthUtils from "../../utils/auth.utils";

export default {
  name: "ForgotPassword",
  data() {
    return {
      email: "",
      errors: {},
      successMessage: "",
      currentLocale: AuthUtils.getCurrentLocale(),
    };
  },

  computed: {
    ...mapGetters(["isLoading", "hasError", "getError"]),

    errorMessage() {
      const storeError = this.$store.getters.getError;
      if (storeError) return storeError;

      // Handle validation errors
      if (Object.keys(this.errors).length > 0) {
        const firstError = Object.values(this.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }

      return "";
    },
  },

  methods: {
    ...mapActions(["requestPasswordReset"]),

    async handleSubmit() {
      // Clear previous errors
      this.errors = {};
      this.successMessage = "";

      // Sanitize email input
      const sanitizedEmail = this.email.trim().toLowerCase();

      // Basic validation
      if (!sanitizedEmail) {
        this.errors = {
          email: ["Email is required"],
        };
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(sanitizedEmail)) {
        this.errors = {
          email: ["Please enter a valid email address"],
        };
        return;
      }

      try {
        await this.$store.dispatch("requestPasswordReset", sanitizedEmail);
        this.successMessage =
          "Password reset link has been sent to your email address.";
      } catch (error) {
        // Handle specific error types
        if (error.status === 422 && error.errors) {
          this.errors = error.errors;
        }
      }
    },
  },

  created() {
    // Check if user is already authenticated
    if (AuthUtils.isAuthenticated()) {
      AuthUtils.redirectAfterLogin();
    }
  },
};
</script>

<style scoped>
.forgot-password-form {
  width: 100%;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: none;
  font-size: 0.9rem;
}

.alert-danger {
  background-color: #fee;
  color: #c53030;
}

.alert-success {
  background-color: #f0fff4;
  color: #38a169;
}

.alert i {
  margin-right: 0.5rem;
}

.auth-form {
  width: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: left;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #fff;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #e53e3e;
}

.form-control:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.invalid-feedback {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.btn {
  padding: 0.875rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.btn-block {
  width: 100%;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.form-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.signin-link {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.link-primary {
  color: #2c3e50;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.link-primary:hover {
  color: #34495e;
  text-decoration: underline;
}
</style>
